<template>
  <FormItem
    :prop="prop"
    :label="floatingLabel ? '' : label"
    :error="errorMessage"
    :class="[
      inlineForm ? 'el-form--inline-form' : '',
      inputNarrow ? 'el-form-item--input-narrow' : '',
      spacing ? 'el-form-item--' + spacing : '',
      radio ? 'el-form-item--radio' : '',
      offset ? 'el-form-item--offset' + offsetSize : '',
      vertical ? 'el-form-item--vertical': '',
      errorStyled ? 'el-form-item--error-styled' : '',
      floatingLabel ? 'floating-label' : '',
      floatingLabelNoColunm ? 'floating-label-no-colunm' : '',
      floatingLabelHide ? 'floating-label--desktop-up-hidden' : '',
      invalid ? 'is-invalid' : '',
      valid ? 'is-valid' : '',
      noMargin ? 'el-form-item--no-margin' : '',
      noLabelBackground ? 'el-form-item--no-label-background' : '',
    ]"
    :without-text-animation="withoutTextAnimation"
    ref="formItem"
    @input:focus="handleFocus"
    @input:blur="handleBlur"
  >
    <template v-if="floatingLabel">
      <div :class="['el-input', focused || hasInputValue || hasForceFocus ? 'is-focused' : '']">
        <slot />
        <span :class="['el-input__label', focused || hasInputValue || hasForceFocus ? 'is-floating' : '']">
          <slot name="custom-label" />
          <slot name="label-link" />
          {{ label }}
          <Tooltip
            v-if="tooltipContent"
            effect="blue"
            :content="tooltipContent"
            placement="top"
          >
            <i
              class="icon icon-question-circle"
              style="margin-left: 5px; cursor: help;"
            />
          </Tooltip>
        </span>
      </div>
    </template>
    <template v-else-if="!floatingLabel">
      <template slot="label">
        <slot name="label" />
      </template>
      <slot />
    </template>

    <div
      slot="error"
      slot-scope="slotProps"
      :class="['el-form-item__error', { 'el-form-item__error--fixed': fixedErrorMessage }]"
      :data-test="dataTestError"
    >
      <template v-if="errorMessage">
        <Alert
          :class="[{'el-alert--new-design': errorAlertNewDesign}]"
          :closable="false"
          :show-icon="!errorAlertNewDesign"
          type="error"
          v-if="errorAlert"
        >
          <template slot="title">
            <span
              v-html="errorMessage"
              @click="errorOnClick"
            />
          </template>
        </Alert>
        <span
          v-else
          v-html="errorMessage"
        />
      </template>
      <template v-else-if="errorStyled">
        <InputHelp
          v-if="errorStyled"
          type="box"
          state="error"
        >
          {{ $t(`errors.${slotProps.error}`) }}
        </InputHelp>
      </template>
      <template v-else-if="errorAlert">
        <Alert
          v-if="errorAlert"
          type="error"
          show-icon
          :closable="false"
        >
          <template slot="title">
            <span
              v-html="$t(`errors.${slotProps.error}`)"
            />
          </template>
        </Alert>
      </template>
      <template v-else>
        {{ $t(`errors.${slotProps.error}`) }}
      </template>
    </div>

    <div
      class="el-form-item__success"
      v-if="successMessage && !errorMessage"
    >
      <template v-if="typeof successMessage === 'object'">
        {{ successMessage.text }}
        <Anchor @click="successMessage.link.action">
          {{ successMessage.link.text }}
        </Anchor>
      </template>
      <template v-else>
        {{ successMessage }}
      </template>
    </div>

    <InputHelp v-if="help">
      <span v-html="help" />
    </InputHelp>

    <slot name="custom-help" />
  </FormItem>
</template>

<script>
import {
  both, isNil, isEmpty, complement,
} from 'ramda';
import FormItem from './FormItem.vue';

const isNotNilOrEmpty = both(complement(isNil), complement(isEmpty));

export default {
  components: {
    FormItem,
  },
  props: {
    prop: String,
    label: String,
    inlineForm: Boolean,
    radio: Boolean,
    offset: [Boolean, String],
    vertical: Boolean,
    spacing: {
      type: String,
    },
    errorMessage: String,
    help: String,
    inputNarrow: Boolean,
    errorStyled: {
      type: Boolean,
      default: false,
    },
    errorAlert: {
      type: Boolean,
      default: false,
    },
    errorAlertNewDesign: {
      type: Boolean,
      default: false,
    },
    errorOnClick: {
      type: Function,
      default() {},
    },
    fixedErrorMessage: {
      type: Boolean,
      default: false,
    },
    floatingLabel: {
      type: Boolean,
      default: false,
    },
    floatingLabelHide: {
      type: Boolean,
      default: false,
    },
    hasForceFocus: {
      type: Boolean,
      default: false,
    },
    inputValue: {
      type: null,
      default: false,
    },
    tooltipContent: String,
    floatingLabelNoColunm: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    dataTestError: {
      type: String,
      default: 'form_input_error_message',
    },
    dataTestValid: {
      type: String,
      default: 'form_input_valid_message',
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
    noLabelBackground: {
      type: Boolean,
      default: false,
    },
    successMessage: {
      type: [String, Object],
      default: '',
    },
    valid: {
      type: Boolean,
      default: false,
    },
    withoutTextAnimation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focused: false,
    };
  },
  computed: {
    hasInputValue() {
      return isNotNilOrEmpty(this.inputValue);
    },
    offsetSize() {
      if (this.offset === true) {
        return '';
      }
      return `--${this.offset}`;
    },
  },
  methods: {
    handleFocus() {
      this.focused = true;
    },
    handleBlur() {
      this.focused = false;
    },
  },
};
</script>

<style lang="scss">
.nowrap {
  white-space: nowrap;
}

.el-form-item {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: rem(20px);
  &::before,
  &::after {
    display: none;
  }

  &.is-success {
    & .el-input__inner,
    & .el-textarea__inner {
      border-color: $--input-border-color;

      &:focus {
        border-color: $--color-primary;
      }
    }
  }

  &.is-required:not(.is-no-asterisk) {
    > .el-form-item__label {
      position: relative;
      &::before {
        content: none;
      }
    }
  }

  &.is-invalid,
  &.is-error {
    .el-input__inner,
    .el-input__inner:focus {
      border-color: $--color-danger-2;
    }

    .el-form-item__error {
      color: $--color-danger-2;
    }
  }

  &.is-valid {
    .el-input__inner,
    .el-input__inner:focus {
      border-color: $--color-success-2;
    }

    .el-form-item__success {
      color: $--color-success-3;

      a {
        color: $--color-success-3;
        font-weight: 600;
      }
    }
  }
}

.el-form-item--disabled {
  .el-form-item__label {
    text-decoration: line-through;
  }
}

.el-form-item--input-narrow {
  .el-form-item__content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  .el-input {
    max-width: rem(175px);
  }

  .el-input,
  .el-button {
    margin-right: rem(15px);
  }

  .el-form-item__help {
    margin-top: rem(5px);
    margin-bottom: rem(5px);
    font-size: rem(15px);
    color: $--color-black;
  }
}

.el-form--inline-form {
  .el-form-item__content {
    @include res(sm-and-up, $--breakpoints-spec) {
      display: flex;
    }

    @include res(xs-only, $--breakpoints-spec) {
      .el-button {
        margin-top: rem(15px);
      }
    }
  }

  .el-input {
    margin-right: rem(10px);
  }
}

.el-form-item--xs {
  .el-form-item__label {
    max-width: 15%;
    flex-basis: 15%;

    @include res(xs-only, $--breakpoints-spec) {
      max-width: 100%;
      flex-basis: 100%;
    }

    & + .el-form-item__content {
      max-width: 76%;
      flex-basis: 76%;

      @include res(xs-only, $--breakpoints-spec) {
        max-width: 100%;
        flex-basis: 100%;
      }
    }
  }
}

.el-form-item--xs-full {
  .el-form-item__label {
    max-width: 15%;
    flex-basis: 15%;

    @include res(xs-only, $--breakpoints-spec) {
      max-width: 100%;
      flex-basis: 100%;
    }

    & + .el-form-item__content {
      max-width: 85%;
      flex-basis: 85%;

      @include res(xs-only, $--breakpoints-spec) {
        max-width: 100%;
        flex-basis: 100%;
      }
    }
  }
}

.el-form-item--sm {
  .el-form-item__label {
    max-width: 24%;
    flex-basis: 24%;

    @include res(xs-only, $--breakpoints-spec) {
      max-width: 100%;
      flex-basis: 100%;
    }

    & + .el-form-item__content {
      max-width: 76%;
      flex-basis: 76%;

      @include res(xs-only, $--breakpoints-spec) {
        max-width: 100%;
        flex-basis: 100%;
      }
    }
  }
}

.el-form-item--lg {
  .el-form-item__label {
    max-width: 40%;
    flex-basis: 40%;

    @include res(xs-only, $--breakpoints-spec) {
      max-width: 100%;
      flex-basis: 100%;
    }

    & + .el-form-item__content {
      max-width: 60%;
      flex-basis: 60%;

      @include res(xs-only, $--breakpoints-spec) {
        max-width: 100%;
        flex-basis: 100%;
      }
    }
  }
}

.el-form-item--radio,
.el-form-item--switch {
  .el-form-item__content {
    padding-top: rem(9px);
  }
}

.el-form-item--offset {
  padding-left: 24%;

  &--large {
    padding-left: 34%;
  }
  &--small {
    padding-left: 15%;

    @include res(xs-only, $--breakpoints-spec) {
      padding-left: 0;
    }
  }

  @include res(xs-only, $--breakpoints-spec) {
    padding-left: 0;
  }
}

.el-form-item--vertical {
  display: block;

  .el-form-item__label {
    max-width: 100%;
    & + .el-form-item__content {
      max-width: 100%;
    }
  }
}

.el-form-item--no-margin {
  margin-bottom: 0;
}

.el-form-item__content {
  line-height: normal;
  float: none;
  width: 100%;

  &::before,
  &::after {
    display: none;
  }
}

.el-form-item__error {
  position: static;

  .el-alert--new-design.is-light {
    background-color: $--color-white;
    color: $--color-danger-2;
    line-height: 20px;
    padding: 2px 7px;

    a {
      color: inherit;
      font-weight: 600;
    }
  }
}

.el-form-item__label {
  font-weight: 700;
  float: none;
  padding-top: rem(9px);
  text-align: left;
  line-height: 1.4;
  max-width: 34%;
  flex-basis: 34%;

  @include res(xs-only, $--breakpoints-spec) {
    margin-bottom: rem(3);
    padding-top: 0;
    max-width: 100%;
    flex-basis: 100%;
  }

  & + .el-form-item__content {
    max-width: 66%;
    flex-basis: 66%;

    @include res(xs-only, $--breakpoints-spec) {
      max-width: 100%;
      flex-basis: 100%;
    }
  }
}

.el-form-item--error-styled {
  .el-form-item__content {
    display: flex;
    flex-wrap: wrap;
  }

  .el-input {
    width: 148px;
    order: 1;
  }

  .el-form-item__error {
    padding-top: 0;
    order: 2;
  }

  .el-form-item__help {
    width: 100%;
    order: 3;
  }
}

.input-no-arrows {
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    appearance: none;
    margin: 0;
  }
}

.floating-label {
  position: relative;

  .el-input__inner {
    font-weight: 600;
    border-radius: 8px;
    padding: 1.6em .8rem;
  }

  .el-select .el-input--suffix .el-input__inner {
    padding-right: 28px;
  }

  .el-input__inner--small {
    padding: 0 .8rem;
  }

  .el-form-item__label {
    font-weight: 400;
    position: absolute;
    top: -12px;
    left: 8px;
    background: #ffffff;
    padding: 0 .2rem;
    z-index: 1;
  }

  .el-form-item__content {
    width: 100%;
    position: relative;
  }

  .el-textarea__inner {
    font-weight: 600;
  }

  &.disabled,
  &[disabled] {
    .el-input__label.is-floating {
      background: linear-gradient(180deg, $--color-white 11px, #f5f7fa 13px, #f5f7fa 100%);
      color: $--color-text-blue;
    }
  }

  &.el-form-item--no-label-background {
    .el-input--extra-small + .el-input__label.is-floating {
      background: linear-gradient(180deg, transparent 5px, $--color-white 6px);
    }
  }
}

.el-input__label {
  position: absolute;
  transform: scale(1);
  left: 8px;
  top: 15px;
  transition: transform .2s, top .2s, font-size .2s;
  color: $--color-text-regular;
  background: #ffffff;
  padding: 0 .2rem;
  pointer-events: none;
  max-width: 91%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;

  .icon-question-circle {
    display: flex;
    color: $--color-primary;
    font-size: 1.4rem;
    margin-left: 5px;
    pointer-events: auto;
  }

  a {
    color: $blue-light;
  }
}

.el-input {
  &.is-focused .el-input__label {
    top: -17px;
    max-width: 98%;
    transform: translate3d(-3px, 6px, 0);
    color: $--color-text-regular;
    font-size: .9rem;
    pointer-events: auto;

    .icon-question-circle {
      color: $--color-primary;
    }

    a {
      color: $--color-primary;
    }
  }

  &:not(.is-focused) {
    .el-input--prefix + .el-input__label {
      left: 46px;
    }
  }
}

.floating-label-no-colunm {
  margin-top: 2rem;
  .el-input.is-focused .el-input__label {
    transform: translate3d(-7px, -6px, 0);
  }
}

.el-form-item__success,
.el-form-item__error {
  font-size: rem(13px);
  line-height: rem(20px);
  padding: 0 12px;
  margin-top: rem(4px);
}

.el-form-item__error--fixed {
  position: absolute;
  margin-top: 0;
}

// ------------------------------ NEW DESIGN ------------------------------

.el-form-item--new-design {
  &.el-form-item--radio {
    .el-form-item__content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      padding-top: 0;
      gap: 12px;

      @media only screen and (max-width: $--breakpoint-sm - 1px) {
        flex-direction: column;
        align-items: flex-start;
        padding-left: 4px;
      }
    }
  }
}
</style>
